import { Box, Flex, Grid, Link, Text } from '@chakra-ui/react';
import {
  CalendarPlus,
  HouseSimple,
  type IconProps,
  Question,
  SignIn,
  User,
} from '@phosphor-icons/react';
import {
  BOOK_PATH,
  HOME_PATH,
  PROFILE_PATH,
  SIGN_IN_PATH,
  SUPPORT_PATH,
  useAuth,
} from '@supsupclub/ui-components';
import { type ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation } from 'react-router-dom';

import { AnimatedBox } from '../../../common/components/animated-box';
import { useShouldCompleteProfile } from '../../../user/utils';

type MenuItemProps = {
  to: string;
  label: string;
  icon: ComponentType<IconProps>;
  isSelected: boolean;
  disabled?: boolean;
};

const MenuItem = ({
  to,
  label,
  icon: Icon,
  isSelected,
  disabled,
}: MenuItemProps) => (
  <Link
    asChild
    display="flex"
    flexDirection="column"
    alignItems="center"
    justifyContent="center"
    gap="3px"
    focusRing="none"
    h="full"
    position="relative"
    bg="transparent"
    color={isSelected ? 'brand.500' : 'gray.500'}
    css={{
      transition: 'color 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
      '&:hover': {
        color: isSelected ? 'brand.600' : 'gray.600',
      },
    }}
  >
    <NavLink className={disabled ? 'disabled' : undefined} to={to}>
      <Box
        h="3px"
        borderRadius="full"
        bg="brand.500"
        position="absolute"
        top="0"
        left="50%"
        css={{
          width: isSelected ? '16px' : '0px',
          opacity: isSelected ? 1 : 0,
          transform: 'translateX(-50%)',
          transition:
            'width 0.4s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.3s ease',
        }}
      />
      <Flex
        align="center"
        justify="center"
        w="48px"
        h="30px"
        borderRadius="full"
        bg={isSelected ? 'brand.50' : 'transparent'}
        css={{
          transition:
            'background 0.3s cubic-bezier(0.16, 1, 0.3, 1), transform 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
          transform: isSelected ? 'translateY(-1px)' : 'translateY(0)',
        }}
      >
        <Icon size={22} weight={isSelected ? 'fill' : 'regular'} />
      </Flex>
      <Text
        fontSize="10px"
        lineHeight="1"
        fontWeight={isSelected ? 'semibold' : 'medium'}
        css={{
          transition:
            'transform 0.3s cubic-bezier(0.16, 1, 0.3, 1), font-weight 0.3s ease',
          transform: isSelected ? 'translateY(-1px)' : 'translateY(0)',
        }}
      >
        {label}
      </Text>
    </NavLink>
  </Link>
);

export const Menu = () => {
  const { t } = useTranslation('common');
  const { isLoggedIn, isUSCAccount } = useAuth();
  const isProfileIncomplete = useShouldCompleteProfile();
  const disabled = isProfileIncomplete;
  const { pathname } = useLocation();

  const items: (MenuItemProps | null)[] = [
    {
      to: HOME_PATH,
      label: t('menu.home'),
      icon: HouseSimple,
      isSelected: pathname === HOME_PATH,
      disabled,
    },
    !isUSCAccount
      ? {
          to: BOOK_PATH,
          label: t('menu.booking'),
          icon: CalendarPlus,
          isSelected: pathname.includes(BOOK_PATH),
          disabled,
        }
      : null,
    {
      to: SUPPORT_PATH,
      label: t('menu.support'),
      icon: Question,
      isSelected: pathname === SUPPORT_PATH,
      disabled,
    },
    isLoggedIn
      ? isUSCAccount
        ? null
        : {
            to: PROFILE_PATH,
            label: t('menu.profile'),
            icon: User,
            isSelected: pathname === PROFILE_PATH,
          }
      : {
          to: SIGN_IN_PATH,
          label: t('menu.signIn'),
          icon: SignIn,
          isSelected: pathname === SIGN_IN_PATH,
        },
  ];

  const visibleItems = items.filter(
    (item): item is MenuItemProps => item !== null,
  );

  return (
    <AnimatedBox
      animation="slideUp"
      delay={0.2}
      p="8px 8px 16px"
      w="full"
      h="full"
    >
      <Box
        borderRadius="full"
        overflow="hidden"
        bg="rgba(255, 255, 255, 0.9)"
        backdropFilter="blur(24px) saturate(200%)"
        h="full"
        boxShadow="0 -4px 30px rgba(122, 107, 255, 0.06), 0 2px 12px rgba(0, 0, 0, 0.04), inset 0 1px 0 rgba(255, 255, 255, 0.7)"
        border="1px solid"
        borderColor="rgba(122, 107, 255, 0.08)"
      >
        <Grid
          templateColumns={`repeat(${visibleItems.length}, 1fr)`}
          gap="0"
          w="full"
          h="full"
        >
          {visibleItems.map((item) => (
            <MenuItem key={item.to} {...item} />
          ))}
        </Grid>
      </Box>
    </AnimatedBox>
  );
};
